/**
 * The admin website: Mohit's, and nobody else's. A site of its own, apart from
 * the student portal, decided 27 September 2026.
 *
 * Signing in is Clerk's, with an emailed code to an address that already
 * exists; the sign in here never creates an account and has no Google button.
 * What makes someone an admin is the API's list, ADMIN_CLERK_USER_IDS, and the
 * API answers the admin routes only for this site's own origin. So the page
 * asks the API before showing anything: a Clerk user who is not an admin is
 * told so and signed out at once, and sees nothing of the list.
 */

import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";
import { http, setTokenSource, type Transport } from "../../web/src/api";
import { AuthLayout, Boot } from "../../web/src/auth/AuthLayout";
import { loadClerk, type ClerkLike } from "../../web/src/auth/clerk";
import { DRAWER_QUERY, useMedia } from "../../web/src/shell/Shell";
import { readTheme, saveTheme, type ThemeChoice } from "../../web/src/shell/theme";
import { Button, Icon, Select, Sidebar, Wordmark } from "../../web/src/ui";
import { SignIn } from "../../web/src/auth/SignIn";
import { adminErrorContract, pendingListResponseContract } from "../../web/src/contracts.generated";
import { usePlace } from "../../web/src/shell/router";
import { Approvals } from "./Approvals";
import { Interns } from "./Interns";
import { Spend } from "./Spend";

/** The admin site's pages, in its sidebar's order. */
export const ADMIN_PAGES = [
  { id: "approvals", path: "/", title: "Approvals", icon: "person" },
  { id: "interns", path: "/interns", title: "Interns", icon: "cap" },
  { id: "spend", path: "/spend", title: "AI spend", icon: "layers" },
] as const;

export type AdminPageId = (typeof ADMIN_PAGES)[number]["id"];

export function adminPageAt(pathname: string): AdminPageId {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return ADMIN_PAGES.find((page) => page.path === path)?.id ?? "approvals";
}

export type AdminState =
  | { readonly kind: "loading" }
  | { readonly kind: "signed_out"; readonly notAdmin: boolean }
  | { readonly kind: "admin" }
  | { readonly kind: "unavailable"; readonly detail: string };

/** Who is at this browser, as the API sees them on this site. Throws nothing. */
export async function resolveAdmin(clerk: ClerkLike, api: Transport = http): Promise<AdminState> {
  if (!clerk.session) return { kind: "signed_out", notAdmin: false };
  try {
    const reply = await api.get("/admin/access");
    if (reply.status === 200 && pendingListResponseContract.safeParse(reply.body).success) return { kind: "admin" };
    const problem = adminErrorContract.safeParse(reply.body);
    const notAdmin = problem.success && problem.data.error === "not_admin";
    if (notAdmin || reply.status === 401) {
      await clerk.signOut().catch(() => {});
      return { kind: "signed_out", notAdmin };
    }
    return { kind: "unavailable", detail: `The API answered ${reply.status}.` };
  } catch (error) {
    return { kind: "unavailable", detail: error instanceof Error ? error.message : "The API could not be reached." };
  }
}

export interface AdminViewProps {
  readonly state: AdminState;
  readonly clerk: ClerkLike | null;
  readonly onSignedIn: () => void;
  readonly onSignOut: () => void;
  readonly onRetry: () => void;
  /** Where the browser is: /interns is Interns, /spend AI spend, anything else Approvals. */
  readonly pathname?: string;
}

export function AdminView(props: AdminViewProps) {
  const { state } = props;
  if (state.kind === "loading") return <Boot />;
  if (state.kind === "unavailable") {
    return (
      <AuthLayout brand={<AdminBrand />}>
        <div class="auth-step">
          <h1>Not reachable</h1>
          <p class="auth-lede">{state.detail}</p>
          <div class="auth-actions">
            <Button variant="primary" block onClick={props.onRetry}>
              Try again
            </Button>
          </div>
        </div>
      </AuthLayout>
    );
  }
  if (state.kind === "signed_out") {
    if (!props.clerk) return <Boot />;
    return (
      <AuthLayout brand={<AdminBrand />}>
        {state.notAdmin ? (
          <p class="notice bad auth-alert" role="alert">
            That account is not an admin, so it was signed out.
          </p>
        ) : null}
        <SignIn clerk={props.clerk} reason={null} onSignedIn={props.onSignedIn} adminOnly />
      </AuthLayout>
    );
  }
  const page = adminPageAt(props.pathname ?? "/");
  return (
    <AdminFrame current={page} onSignOut={props.onSignOut}>
      {page === "interns" ? <Interns /> : page === "spend" ? <Spend /> : <Approvals />}
    </AdminFrame>
  );
}

const THEMES: readonly { value: ThemeChoice; label: string }[] = [
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
  { value: "system", label: "System" },
];

/** The admin site's brand: Hallquill's wordmark, and what this site is. */
export function AdminBrand() {
  return (
    <span class="admin-brand">
      <Wordmark />
      <span class="admin-tag">Admin</span>
    </span>
  );
}

/**
 * The admin site in Harvey's frame (day 34), as the portal's: the dark sidebar
 * with the site's pages, the admin's row opening the theme and Sign out, and
 * a drawer at phone width. Approvals, and since day 35 Interns; the admin
 * days that follow (the queues) add theirs to ADMIN_PAGES.
 */
export function AdminFrame(props: { readonly current?: AdminPageId; readonly onSignOut: () => void; readonly children: ComponentChildren }) {
  const drawer = useMedia(DRAWER_QUERY);
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [theme, setTheme] = useState<ThemeChoice>(readTheme);
  const hidden = drawer ? !open : collapsed;
  const toggle = () => (drawer ? setOpen((now) => !now) : setCollapsed((now) => !now));
  const classes = ["app", "admin"];
  if (drawer && open) classes.push("nav-open");
  if (!drawer && collapsed) classes.push("nav-closed");
  return (
    <div class={classes.join(" ")}>
      <Sidebar
        id="sidebar"
        brand={<AdminBrand />}
        items={ADMIN_PAGES.map((page) => ({ kind: "top" as const, id: page.id, label: page.title, href: page.path, icon: page.icon }))}
        current={props.current ?? "approvals"}
        hidden={hidden}
        onClose={toggle}
        onFollow={() => setOpen(false)}
        me={{
          initials: "A",
          name: "Admin",
          detail: "Hallquill",
          menu: (
            <>
              <div class="menu-row">
                <label for="menu-theme">Theme</label>
                <Select
                  id="menu-theme"
                  size="sm"
                  up
                  options={THEMES}
                  value={theme}
                  onChange={(value) => {
                    const choice = value as ThemeChoice;
                    saveTheme(choice);
                    setTheme(choice);
                  }}
                />
              </div>
              <hr class="menu-line" />
              <button type="button" class="menu-item admin-signout" onClick={props.onSignOut}>
                <Icon name="leave" />
                Sign out
              </button>
            </>
          ),
        }}
      />
      {drawer && open ? <div class="scrim" aria-hidden="true" onClick={() => setOpen(false)} /> : null}
      <div class="main">
        <div class="topbar">
          <button type="button" class="icon-btn topbar-menu" aria-label="Open menu" title="Open menu" aria-controls="sidebar" aria-expanded={!hidden} onClick={toggle}>
            <Icon name="panel" />
          </button>
          <AdminBrand />
        </div>
        <main class="content admin-main" id="content" tabIndex={-1}>
          {props.children}
        </main>
      </div>
    </div>
  );
}

export function AdminApp() {
  const place = usePlace();
  const [state, setState] = useState<AdminState>({ kind: "loading" });
  const [clerk, setClerk] = useState<ClerkLike | null>(null);

  const check = async () => {
    setState({ kind: "loading" });
    try {
      const loaded = await loadClerk();
      setTokenSource(async () => (loaded.session ? await loaded.session.getToken() : null));
      setClerk(loaded);
      setState(await resolveAdmin(loaded));
    } catch (error) {
      setState({ kind: "unavailable", detail: error instanceof Error ? error.message : "Sign in could not start." });
    }
  };

  useEffect(() => {
    void check();
  }, []);

  useEffect(() => {
    const page = ADMIN_PAGES.find((each) => each.id === adminPageAt(place.pathname));
    document.title = state.kind === "admin" && page ? `${page.title} · Hallquill Admin` : "Hallquill Admin";
    if (state.kind === "admin") document.getElementById("content")?.focus({ preventScroll: true });
  }, [state.kind, place.pathname]);

  return (
    <AdminView
      state={state}
      pathname={place.pathname}
      clerk={clerk}
      onSignedIn={() => void check()}
      onRetry={() => void check()}
      onSignOut={() => {
        if (!clerk) return;
        void clerk.signOut().finally(() => setState({ kind: "signed_out", notAdmin: false }));
      }}
    />
  );
}
